import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

import type { ChatState } from './reducer';
import type { PersistedEvent } from './replay';
import type { ChatEffect, ChatEvent, ChatScript } from './types';
import type { Audience } from '@/cms/topics';
import { track } from '@/lib/analytics';
import { useChatProgressStore } from '@/store/chat-progress';
import { useUserStore } from '@/store/user';
import { usePacing } from './pacing';
import { chatReducer, clearEffects, initialChatState, isMidModule, moduleProgress, runScore } from './reducer';
import { persistedEventFor, resumableRun } from './replay';
import { roundScore } from './scoring';

/**
 * A module run: one Zustand store per mounted chat, wrapping the pure reducer
 * and draining its effects into the persisted user store and GA4.
 *
 * The store is created per run rather than as a module singleton because a
 * module's state is genuinely per-visit. What survives a visit is the
 * learner's *inputs*, in `chat-progress.ts`: re-entering an unfinished module
 * replays them instantly, so leaving halfway — or switching language — puts the
 * learner back exactly where they were.
 */

export type ChatStoreState = {
  chat: ChatState;
  dispatch: (event: ChatEvent) => void;
  /** Installs a run rebuilt by `replayRun`, and drains the effects it emitted. */
  resume: (chat: ChatState, effects: Array<ChatEffect>) => void;
};

export type ChatStoreApi = ReturnType<typeof createChatStore>;

/**
 * `replaying` is true for the effects a resume re-derives. They must still
 * reach the counters — `startModule` has just zeroed them — but must never
 * reach analytics, which recorded them when they first happened.
 */
export type ChatEffectHandler = (effects: Array<ChatEffect>, options: { replaying: boolean }) => void;

export type ChatStoreOptions = {
  handleEffects: ChatEffectHandler;
  /**
   * Every learner input the reducer accepted, in a persistable form, with the
   * state it produced — so a caller can save the input and how far the run had
   * played in the same breath.
   */
  onInput?: (event: PersistedEvent, next: ChatState) => void;
};

export function createChatStore({ handleEffects, onInput }: ChatStoreOptions) {
  return createStore<ChatStoreState>()((set, get) => ({
    chat: initialChatState,

    dispatch: (event) => {
      const previous = get().chat;
      const next = chatReducer(previous, event);

      if (next === previous) {
        return;
      }

      set({ chat: clearEffects(next) });

      // Read off the prompt as it stood *before* the dispatch: that is the one
      // that gives the tapped option its position.
      const input = onInput ? persistedEventFor(previous.pending ?? previous.staged, event) : null;

      if (input && onInput) {
        onInput(input, next);
      }

      if (next.effects.length > 0) {
        handleEffects(next.effects, { replaying: false });
      }
    },

    resume: (chat, effects) => {
      set({ chat: clearEffects(chat) });

      if (effects.length > 0) {
        handleEffects(effects, { replaying: true });
      }
    },
  }));
}

export type ChatRunOptions = {
  script: ChatScript;
  language: string;
  audience: Audience;
  /**
   * The run's identity. Defaults to the module key; pass the topic document's
   * id where one module has a document per language, so switching language
   * rebuilds the run against the new script instead of leaving the old thread
   * on screen.
   */
  runKey?: string;
};

/**
 * Mounts a module run: seeds the user store's counters, resumes a saved run if
 * there is one, plays the script at the pacing the learner's motion preference
 * allows, and reports every event in the analytics contract.
 *
 * Returns the live reducer state and a `dispatch` for the answer control.
 */
export function useChatRun({ script, language, audience, runKey }: ChatRunOptions) {
  const { moduleKey } = script.meta;
  const runId = runKey ?? moduleKey;

  // Effects are performed against the latest props without re-creating the
  // store, which would restart the run on every render.
  const context = useRef({ script, language, audience });
  context.current = { script, language, audience };

  const store = useMemo(() => {
    const handleEffects: ChatEffectHandler = (effects, { replaying }) => {
      const { completeSlice, recordAttempt, finishModule } = useUserStore.getState();
      const current = context.current;
      const key = current.script.meta.moduleKey;

      for (const effect of effects) {
        switch (effect.kind) {
          case 'slice_completed':
            completeSlice(key);
            break;

          case 'attempt':
            // `user_interaction` and free text report to GA4 but never touch
            // the counters that drive the star rating.
            if (effect.scores) {
              recordAttempt(key, effect.correct);
            }

            if (!replaying) {
              track('quiz_answer', {
                module_key: key,
                language: current.language,
                audience: current.audience,
                question_id: effect.questionId,
                question_index: effect.questionIndex,
                question_type: effect.questionType,
                correct: effect.correct,
                attempt: effect.attempt,
              });
            }
            break;

          case 'module_completed':
            if (replaying) {
              break;
            }

            track('topic_complete', {
              module_key: key,
              language: current.language,
              audience: current.audience,
              // The precision every score is shown at; the float is noise in GA4.
              score: roundScore(effect.score),
            });
            finishModule(key, effect.score);
            // A finished module starts over on re-entry, exactly as before —
            // `bestScore` in the user store is what remembers it was done.
            useChatProgressStore.getState().clear(key);
            break;
        }
      }
    };

    return createChatStore({
      handleEffects,
      onInput: (event, next) => {
        const current = context.current;
        // Only the two counted fields are persisted: `fraction` is derived, and
        // storing it would put a value in `localStorage` that the run schema
        // does not declare.
        const { stepsDone, stepsTotal } = moduleProgress(current.script, next);

        useChatProgressStore
          .getState()
          .record(current.script.meta.moduleKey, current.language, event, { stepsDone, stepsTotal });
      },
    });
    // A new run means a new script: the store is rebuilt from scratch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  const chat = useStore(store, (state) => state.chat);
  const dispatch = useStore(store, (state) => state.dispatch);

  const advance = useCallback(() => {
    dispatch({ type: 'advance' });
  }, [dispatch]);

  useEffect(() => {
    const progress = useChatProgressStore.getState();
    const decision = resumableRun(script, progress.runs[moduleKey]);

    if (decision.action === 'start') {
      if (decision.discarded !== null) {
        console.warn(`[chat] discarding the saved run for "${moduleKey}": ${decision.discarded}`);
      }

      progress.clear(moduleKey);
    }

    // Entering a module resets its counters and keeps its best score —
    // the current app's `startTopic` semantics, verbatim. A resume rebuilds
    // them from the replayed effects in the same tick.
    useUserStore.getState().startModule(moduleKey, script.steps.length);
    track('topic_start', { module_key: moduleKey, language, audience });

    if (decision.action === 'resume') {
      if (decision.dropped !== null) {
        console.warn(`[chat] resuming "${moduleKey}" from input ${decision.kept}: ${decision.dropped}`);
        progress.keep(moduleKey, decision.kept);
      }

      store.getState().resume(decision.state, decision.effects);
    } else {
      store.getState().dispatch({ type: 'start', script });
    }

    return () => {
      const { chat: state } = store.getState();

      if (isMidModule(state)) {
        track('topic_abandon', {
          module_key: moduleKey,
          language,
          checkpoint_index: state.thread.filter((message) => message.kind === 'marker').length,
        });
      }
    };
    // `script` is stable for a given run; `runId` is the identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // Keeps the saved run's position level with what the module header is
  // drawing. Reporting only on a learner input left the journey a step or more
  // behind the rail inside the module, which is the same progress in two
  // places disagreeing. Runs once on mount — after the effect above has
  // started or resumed the run — so a run saved before `progress` existed
  // gains it simply by being opened.
  useEffect(() => {
    const persist = () => {
      const { stepsDone, stepsTotal } = moduleProgress(context.current.script, store.getState().chat);

      useChatProgressStore.getState().saveProgress(moduleKey, { stepsDone, stepsTotal });
    };

    persist();

    return store.subscribe(persist);
  }, [moduleKey, store]);

  const typing = usePacing({ chat, advance });

  return { chat, dispatch, typing, score: runScore(chat) };
}
